import React, { Fragment, useState, useContext, useEffect } from 'react';
import WizardInput from './WizardInput';
import { InputGroup, InputGroupAddon, Input, FormGroup, Label } from 'reactstrap';
import classNames from 'classnames';
import Flag from "react-flags";
import { AuthWizardContext } from '../../../context/Context';
import NumberFormat from 'react-number-format';
import WizardError from './WizardError';
import { cleanVePhone, getVePhoneFormat } from '../../../helpers/utils';

const splitDni = (dni) => {
  const match = /^([EVJPCGevjpcg])[- ]?(\d+)$/.exec(dni || '');
  return match ? { prefix: match[1].toUpperCase(), number: match[2] } : { prefix: 'V', number: '' };
};

const ALPHA_SPACE_PATTERN = /^[\p{L}\s]+$/u;

const AdvanceUserForm = ({ register, errors, setValue, setError, clearErrors, trigger }) => {
  const { handleInputChange, user, setUser } = useContext(AuthWizardContext);
  const initialDni = splitDni(user.dni);

  const [prefix, setPrefix] = useState(initialDni.prefix);
  const [phone, setPhone] = useState(user.phone || '');
  const [dni, setDni] = useState(initialDni.number);
  const [serverFieldErrors, setServerFieldErrors] = useState({});

  register("dni");
  register('phone');

  useEffect(() => {
    setValue('dni', user.dni || '');
    setValue('phone', cleanVePhone(user.phone) || '');
  }, [setValue]);

  // Si llegamos aquí por un dato inválido o duplicado (nombre, apellido, DNI o teléfono) al intentar registrarse, lo resaltamos
  useEffect(() => {
    const { dni: dniMessage, phone: phoneMessage, firstname: firstnameMessage, lastname: lastnameMessage } = user._fieldErrors || {};
    if (dniMessage || phoneMessage || firstnameMessage || lastnameMessage) {
      setServerFieldErrors({ dni: dniMessage, phone: phoneMessage, firstname: firstnameMessage, lastname: lastnameMessage });
      setUser((prev) => {
        const { dni, phone, firstname, lastname, ...remaining } = prev._fieldErrors || {};
        const hasRemaining = Object.keys(remaining).length > 0;
        const { _fieldErrors, ...rest } = prev;
        return hasRemaining ? { ...rest, _fieldErrors: remaining } : rest;
      });
    }
  }, []); // eslint-disable-line

  const dniErrorMessage = errors.dni?.message || serverFieldErrors.dni;
  const phoneErrorMessage = errors.phone?.message || serverFieldErrors.phone;

  const firstnameErrors = serverFieldErrors.firstname && !errors.firstname
    ? { ...errors, firstname: { message: serverFieldErrors.firstname } }
    : errors;
  const lastnameErrors = serverFieldErrors.lastname && !errors.lastname
    ? { ...errors, lastname: { message: serverFieldErrors.lastname } }
    : errors;

  useEffect(() => {
    if (phone) {
      clearErrors('phone');
    } else {
      setError('phone', {
        message: 'El número de teléfono es requerido.'
      });
    }
  }, [phone, setError, clearErrors]);

  useEffect(() => {
    if (dni) {
      clearErrors('dni');
    } else {
      setError('dni', {
        message: 'El DNI es requerido.'
      });
    }
  }, [dni, setError, clearErrors]);

  return (
    <>
      <WizardInput
        label="Nombres"
        placeholder="Nombres"
        name="firstname"
        onChange={({ target }) => { handleInputChange(target); trigger('firstname'); }}
        id="firstname"
        className="input-spin-none"
        innerRef={register({
          required: 'Nombres es obligatorio',
          validate: value => ALPHA_SPACE_PATTERN.test(value) || 'Solo se permiten letras y espacios'
        })}
        errors={firstnameErrors}
      />
      <WizardInput
        label="Apellidos"
        placeholder="Apellidos"
        name="lastname"
        onChange={({ target }) => { handleInputChange(target); trigger('lastname'); }}
        id="lastname"
        className="input-spin-none"
        innerRef={register({
          required: 'Apellidos es obligatorio',
          validate: value => ALPHA_SPACE_PATTERN.test(value) || 'Solo se permiten letras y espacios'
        })}
        errors={lastnameErrors}
      />

      <FormGroup>
        <Label>ID (Cédula o RIF)</Label>
        <InputGroup>
          <InputGroupAddon addonType="prepend">
            <Input type='select' value={prefix} onChange={(e) => {
              setPrefix(e.target.value);
              setValue('dni', `${e.target.value}${dni}`);
              if (serverFieldErrors.dni) setServerFieldErrors((prev) => ({ ...prev, dni: null }));
            }}>
              <option value="V">V</option>
              <option value="E">E</option>
              <option value="J">J</option>
              <option value="P">P</option>
              <option value="C">C</option>
              <option value="G">G</option>
            </Input>
          </InputGroupAddon>
          <NumberFormat
            className={classNames("form-control input-spin-none", { "border-danger": dniErrorMessage })}
            value={dni}
            onValueChange={({ value }) => {
              setValue('dni', `${prefix}${value}`);
              setDni(value);
              if (serverFieldErrors.dni) setServerFieldErrors((prev) => ({ ...prev, dni: null }));
            }}
            thousandSeparator="."
            decimalSeparator="," 
            decimalScale={0}
            getInputRef={register('dni', {
              required: 'DNI es obligatorio'
            })}
            renderText={(value, props) => <div {...props}>{value}</div>}
          />
        </InputGroup>
        <WizardError error={dniErrorMessage} className="mt-1" />
      </FormGroup>

      <FormGroup>
        <Label>Teléfono <small className="text-muted">(ej: 04241234567)</small></Label>
        <InputGroup>
          <InputGroupAddon addonType="prepend">
            <Flag
              name="VE"
              format="png"
              pngSize={32}
              className="mr-2"
              shiny={false}
              basePath="/flags"
              alt="Venezuelan Flag"
            />
          </InputGroupAddon>
          <NumberFormat
            placeholder="04241234567"
            className={classNames("form-control input-spin-none", { "border-danger": phoneErrorMessage })}
            value={phone}
            onValueChange={({ value }) => {
              const cleaned = cleanVePhone(value);
              setValue('phone', cleaned);
              setPhone(value);
              if (serverFieldErrors.phone) setServerFieldErrors((prev) => ({ ...prev, phone: null }));
            }}
            format={getVePhoneFormat(phone)}
            onChange={({ target }) => {
              handleInputChange(target);
            }}
            getInputRef={register('phone', {
              required: 'Teléfono es obligatorio'
            })}
          />
        </InputGroup>
        <WizardError error={phoneErrorMessage} className="mt-1" />
      </FormGroup>

      <WizardInput
        type="textarea"
        label="Dirección"
        name="address"
        rows="4"
        id="address"
        innerRef={register({ required: false })}
        errors={errors}
      />
    </>
  );
};

export default AdvanceUserForm;